import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faPen,
  faToggleOn,
  faToggleOff,
  faCog,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";

const DraggablePost = React.memo(
  ({
    post,
    index,
    handleDelete,
    handleEdit,
    handleToggleActive,
    handleToggleNewMod,
    handleCreatorChange,
    updateDiscount,
    creators = [],
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const settingsButtonRef = useRef(null);

    const [localDiscount, setLocalDiscount] = useState(post.discount ?? 0);

    useEffect(() => {
      setLocalDiscount(post.discount ?? 0);
    }, [post.discount]);

    const getCreatorColor = () => (creators.find((creator) => creator.name === post.creator)?.color || "");
    const getRowColor = () => {
      switch (post.creator) {
        case "Dipanoo":
          return "bg-green-400/20 backdrop-blur border border-green-400/20";
        case "Dannus":
          return "bg-yellow-400/30 backdrop-blur border border-yellow-400/20";
        case "Roasty":
          return "bg-pink-400/30 backdrop-blur border border-pink-400/20";
        default:
          return "bg-blue-400/20 backdrop-blur border border-yellow-400/60";
      }
    };

    const thumbnailImage =
      post.mediaContent?.[0]?.imageUrl ||
      post.imagePath?.[0] ||
      "https://via.placeholder.com/150x200";

    const selectCreator = (creator) => {
      handleCreatorChange(post._id, creator);
      setDropdownOpen(false);
    };

    const toggleDropdown = () => {
      if (settingsButtonRef.current) {
        const rect = settingsButtonRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
      setDropdownOpen((prev) => !prev);
    };

    return (
      <Draggable draggableId={post._id} index={index}>
        {(provided) => (
          <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`
              transition-all duration-200
              hover:shadow-[0_0_18px_6px_rgba(139,92,246,0.6)]
              hover:bg-violet-900
              ${getRowColor()}
            `}
            style={{
              ...provided.draggableProps.style,
              ...(getCreatorColor() ? {
                backgroundColor: getCreatorColor().startsWith("#") ? getCreatorColor() + "33" : "color-mix(in srgb, " + getCreatorColor() + " 18%, transparent)",
                borderColor: getCreatorColor().startsWith("#") ? getCreatorColor() + "66" : getCreatorColor(),
              } : {}),
            }}
          >
            {/* ORDER (DRAG HANDLE – NO CSS CHANGE) */}
            <td
              {...provided.dragHandleProps}
              className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100"
            >
              <span className="bg-gray-600 text-gray-200 px-2 py-1 rounded-full text-xs font-semibold">
                #{post.order}
              </span>
            </td>

            {/* IMAGE */}
            <td className="px-6 py-4 whitespace-nowrap">
              <img
                src={thumbnailImage}
                className="w-16 h-12 object-cover rounded-lg shadow-sm"
                alt={post.title}
              />
            </td>

            {/* TITLE */}
            <td className="px-6 py-4 text-sm text-gray-100 max-w-xs">
              <div className="truncate font-medium">{post.title}</div>
            </td>

            {/* DISCOUNT */}
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-100">
              <input
                type="number"
                min="0"
                max="100"
                value={localDiscount}
                onChange={(e) => setLocalDiscount(e.target.value)}
                onBlur={() =>
                  updateDiscount(post._id, Number(localDiscount))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    updateDiscount(post._id, Number(localDiscount));
                    e.target.blur();
                  }
                }}
                className="w-16 px-2 py-1 rounded bg-gray-700 text-white border border-gray-600 text-center focus:outline-none focus:border-indigo-400"
              />
            </td>

            {/* CATEGORY */}
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
              <span className="bg-blue-900 text-blue-200 px-3 py-1 rounded-full text-xs font-medium">
                {post.category}
              </span>
            </td>

            {/* ACTIVE */}
            <td className="px-6 py-4 whitespace-nowrap">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  post.isActive
                    ? "bg-green-900 text-green-200 border border-green-700"
                    : "bg-red-900 text-red-200 border border-red-700"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full mr-2 ${
                    post.isActive ? "bg-green-400" : "bg-red-400"
                  }`}
                />
                {post.isActive ? "Active" : "Inactive"}
              </span>
            </td>

            {/* NEW MOD */}
            <td className="px-6 py-4 whitespace-nowrap">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  post.newModBanner
                    ? "bg-yellow-900 text-yellow-200 border border-yellow-700"
                    : "bg-gray-600 text-gray-300 border border-gray-500"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full mr-2 ${
                    post.newModBanner ? "bg-yellow-400" : "bg-gray-400"
                  }`}
                />
                {post.newModBanner ? "NEW MOD" : "Normal"}
              </span>
            </td>

            {/* ACTIONS */}
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
              <div className="flex items-center space-x-3">
                <button
                  className={`p-2 rounded-lg ${
                    post.isActive
                      ? "text-green-400 hover:bg-green-900 hover:text-green-300"
                      : "text-gray-500 hover:bg-gray-700 hover:text-gray-300"
                  }`}
                  onClick={() =>
                    handleToggleActive(post._id, post.isActive)
                  }
                >
                  <FontAwesomeIcon
                    icon={post.isActive ? faToggleOn : faToggleOff}
                  />
                </button>

                <button
                  className={`p-2 rounded-lg ${
                    post.newModBanner
                      ? "text-yellow-400 hover:bg-yellow-900 hover:text-yellow-300"
                      : "text-gray-500 hover:bg-gray-700 hover:text-gray-300"
                  }`}
                  onClick={() =>
                    handleToggleNewMod(post._id, post.newModBanner)
                  }
                >
                  <FontAwesomeIcon
                    icon={post.newModBanner ? faToggleOn : faToggleOff}
                  />
                </button>

                <button
                  className="p-2 text-red-400 hover:bg-red-900 hover:text-red-300 rounded-lg"
                  onClick={() => handleDelete(post._id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>

                <Link to={`/Admin/Post/Edit/${post._id}`}>
                  <button className="p-2 text-blue-400 hover:bg-blue-900 hover:text-blue-300 rounded-lg">
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                </Link>

                <div className="relative">
                  <button
                    ref={settingsButtonRef}
                    className="p-2 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg"
                    onClick={toggleDropdown}
                  >
                    <FontAwesomeIcon icon={faCog} />
                  </button>

                  {dropdownOpen &&
                    ReactDOM.createPortal(
                      <div
                        className="w-40 bg-gray-800 border border-gray-700 rounded-lg shadow-lg flex flex-col z-[9999]"
                        style={{
                          position: "absolute",
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                        }}
                      >
                        {creators.map((creator) => (
                          <button
                            key={creator.slug || creator.name}
                            onClick={() => selectCreator(creator.name)}
                            className="px-4 py-2 text-left hover:bg-gray-700"
                            style={{ color: creator.color }}
                          >
                            {creator.name}
                          </button>
                        ))}
                      </div>,
                      document.body
                    )}
                </div>
              </div>
            </td>
          </tr>
        )}
      </Draggable>
    );
  }
);

export default DraggablePost;
